import { useEffect, useState, useMemo, useCallback } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Info, AlertTriangle } from 'lucide-react';

/**
 * Owner Analytics page.
 * Consumes /api/analytics/summary + /api/analytics/filters.
 * Phase 2 scope: Executive KPIs, What Changed, Building table,
 * Vacancy Aging, Leasing Source. Extended sections deferred.
 */

const PRESETS = [
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '60d', label: '60 days', days: 60 },
  { key: '90d', label: '90 days', days: 90 },
  { key: 'mtd', label: 'MTD' },
  { key: 'ytd', label: 'YTD' },
  { key: 'custom', label: 'Custom' },
];

function rangeFor(preset, custom) {
  const end = new Date();
  const start = new Date();
  if (preset === 'mtd') {
    start.setDate(1);
  } else if (preset === 'ytd') {
    start.setMonth(0, 1);
  } else if (preset === 'custom') {
    return {
      start: custom.start ? new Date(custom.start) : new Date(end.getTime() - 30 * 864e5),
      end: custom.end ? new Date(custom.end) : end,
    };
  } else {
    const p = PRESETS.find((x) => x.key === preset);
    start.setTime(end.getTime() - (p?.days ?? 30) * 864e5);
  }
  return { start, end };
}

export default function AnalyticsPage({ portfolioId, portfolioName, onBack }) {
  const [preset, setPreset] = useState('30d');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [building, setBuilding] = useState('');
  const [bdBa, setBdBa] = useState('');
  const [filterOpts, setFilterOpts] = useState({ buildings: [], bd_bas: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!portfolioId) return;
    setLoading(true);
    setError(null);
    try {
      const { start, end } = rangeFor(preset, customRange);
      const params = new URLSearchParams({
        portfolio_id: portfolioId,
        start: start.toISOString(),
        end: end.toISOString(),
      });
      if (building) params.set('building', building);
      if (bdBa) params.set('bd_ba', bdBa);
      const res = await api.get(`/analytics/summary?${params.toString()}`);
      setData(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to load analytics';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, preset, customRange, building, bdBa]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!portfolioId) return;
    api.get(`/analytics/filters?portfolio_id=${portfolioId}`)
      .then((r) => setFilterOpts(r.data))
      .catch(() => { /* non-fatal */ });
  }, [portfolioId]);

  const backfillOnly = data?.data_quality?.has_backfill_only_events;

  return (
    <div className="dashboard-content" data-testid="analytics-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="btn-secondary" onClick={onBack} data-testid="analytics-back-button">
          <ArrowLeft size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
          Back
        </button>
        <h1 style={{ margin: 0, fontSize: 22 }}>Analytics — {portfolioName}</h1>
      </div>

      {backfillOnly && (
        <div
          role="alert"
          style={{
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            fontSize: 13,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
          data-testid="incomplete-history-banner"
        >
          <AlertTriangle size={16} style={{ color: '#b45309', marginTop: 2 }} />
          <div>
            <strong>History tracking just started for this portfolio.</strong>
            {' '}Leasing velocity, absorption, and time-to-lease metrics require
            transitions we haven&rsquo;t observed yet. New events will appear as
            statuses change (AppFolio imports, agent approvals, manual edits).
          </div>
        </div>
      )}

      {/* Filters */}
      <div
        style={{
          background: 'white', border: '1px solid #e2e8f0', borderRadius: 8,
          padding: 12, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap',
          alignItems: 'center',
        }}
        data-testid="analytics-filters"
      >
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              className={preset === p.key ? 'btn-primary' : 'btn-secondary'}
              style={{ padding: '6px 12px', fontSize: 13 }}
              data-testid={`preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <>
            <input type="date" value={customRange.start} onChange={(e) => setCustomRange((r) => ({ ...r, start: e.target.value }))} data-testid="custom-start" />
            <input type="date" value={customRange.end} onChange={(e) => setCustomRange((r) => ({ ...r, end: e.target.value }))} data-testid="custom-end" />
          </>
        )}
        <div style={{ display: 'flex', gap: 8, flex: '1 1 auto', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <select value={building} onChange={(e) => setBuilding(e.target.value)} data-testid="filter-building" style={{ padding: '6px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6 }}>
            <option value="">All buildings</option>
            {filterOpts.buildings.map((b) => (<option key={b} value={b}>{b}</option>))}
          </select>
          <select value={bdBa} onChange={(e) => setBdBa(e.target.value)} data-testid="filter-bdba" style={{ padding: '6px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6 }}>
            <option value="">All unit types</option>
            {filterOpts.bd_bas.map((b) => (<option key={b} value={b}>{b}</option>))}
          </select>
        </div>
      </div>

      {loading && <div style={{ padding: 24, textAlign: 'center' }}>Loading…</div>}
      {error && <div style={{ padding: 24, color: '#dc2626' }}>Error: {error}</div>}
      {data && !loading && (
        <>
          <WhatChanged wc={data.what_changed} />
          <KpiRow kpis={data.kpis} />
          <BuildingsTable buildings={data.buildings} />
          <VacancyAging aging={data.aging} />
          <LeasingSource src={data.leasing_source} />
        </>
      )}
    </div>
  );
}

// ---------- What Changed ----------
function WhatChanged({ wc }) {
  if (!wc) return null;
  const lines = [];
  if (wc.leased > 0) lines.push(`${wc.leased} unit${wc.leased === 1 ? '' : 's'} leased`);
  if (wc.became_vacant > 0) lines.push(`${wc.became_vacant} unit${wc.became_vacant === 1 ? '' : 's'} became vacant`);
  lines.push(`Net absorption: ${wc.net_absorption >= 0 ? '+' : ''}${wc.net_absorption}`);
  if (wc.newly_stale_units > 0) lines.push(`${wc.newly_stale_units} unit${wc.newly_stale_units === 1 ? '' : 's'} crossed 30 days vacant`);
  if (wc.internal_leases > 0) lines.push(`${wc.internal_leases} internal lease${wc.internal_leases === 1 ? '' : 's'}`);
  if (wc.external_agent_leases > 0) lines.push(`${wc.external_agent_leases} external-agent lease${wc.external_agent_leases === 1 ? '' : 's'}`);
  if (wc.holds_created > 0) lines.push(`${wc.holds_created} external-agent hold${wc.holds_created === 1 ? '' : 's'} created`);
  lines.push(`Vacant-Ready inventory: ${wc.vacant_ready_now}`);
  return (
    <div className="dashboard-section" data-testid="what-changed">
      <h2>What Changed — Last 7 Days</h2>
      <ul style={{ marginTop: 8, paddingLeft: 20, lineHeight: 1.7, fontSize: 14, color: '#334155' }}>
        {lines.map((l, i) => <li key={i}>{l}</li>)}
      </ul>
    </div>
  );
}

// ---------- KPI Row ----------
function KpiRow({ kpis }) {
  if (!kpis) return null;
  const cards = [
    { label: 'Occupancy', value: `${kpis.occupancy_pct}%`, test: 'kpi-occupancy' },
    { label: 'Vacant-Ready', value: kpis.vacant_ready_now, test: 'kpi-vr' },
    { label: 'Units Leased', value: kpis.units_leased_in_period, test: 'kpi-leased', hint: 'transitions → Current in period' },
    { label: 'Net Absorption', value: (kpis.net_absorption >= 0 ? '+' : '') + kpis.net_absorption, test: 'kpi-net-absorb' },
    { label: 'Absorption Rate', value: `${kpis.absorption_rate_pct}%`, test: 'kpi-absorb-rate' },
    { label: 'Median Days to Lease', value: kpis.median_days_to_lease ?? '—', test: 'kpi-median-dtl' },
    { label: 'Stale Inventory', value: `${kpis.stale_inventory_pct}%`, test: 'kpi-stale', hint: '>30 days Vacant-Ready' },
    { label: 'Weeks of Supply', value: kpis.weeks_of_supply ?? '—', test: 'kpi-wos' },
  ];
  return (
    <div className="dashboard-section" data-testid="kpi-row">
      <h2>Executive KPIs</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginTop: 8 }}>
        {cards.map((c) => (
          <div key={c.label} data-testid={c.test} style={{
            border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#f8fafc',
          }}>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 4 }}>
              {c.label}
              {c.hint && <span title={c.hint}><Info size={11} style={{ color: '#94a3b8' }} /></span>}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Buildings ----------
function BuildingsTable({ buildings }) {
  const [sortKey, setSortKey] = useState('occupancy_pct');
  const [sortDir, setSortDir] = useState('desc');
  const sorted = useMemo(() => {
    const rows = [...(buildings || [])];
    rows.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1; if (bv == null) return -1;
      if (av === bv) return 0;
      return (av > bv ? 1 : -1) * (sortDir === 'asc' ? 1 : -1);
    });
    return rows;
  }, [buildings, sortKey, sortDir]);
  const toggle = (k) => {
    if (sortKey === k) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };
  if (!buildings || buildings.length === 0) return null;
  const heat = (v, kind) => {
    if (v == null) return {};
    if (kind === 'occupancy_pct') {
      const c = v >= 90 ? '#dcfce7' : v >= 75 ? '#dbeafe' : v >= 50 ? '#fef3c7' : '#fee2e2';
      return { background: c };
    }
    if (kind === 'stale_pct') {
      const c = v === 0 ? '#dcfce7' : v <= 25 ? '#fef3c7' : '#fee2e2';
      return { background: c };
    }
    return {};
  };
  const cols = [
    { key: 'address', label: 'Building', kind: 'text' },
    { key: 'units', label: 'Units' },
    { key: 'vacant', label: 'Vacant' },
    { key: 'vacant_ready', label: 'Vacant-Ready' },
    { key: 'leased_in_period', label: 'Leased' },
    { key: 'became_vacant_in_period', label: 'Became Vacant' },
    { key: 'net_absorption', label: 'Net Absorb.' },
    { key: 'occupancy_pct', label: 'Occupancy %', heat: 'occupancy_pct' },
    { key: 'absorption_rate_pct', label: 'Absorption %' },
    { key: 'median_days_to_lease', label: 'Median DTL' },
    { key: 'stale_pct', label: 'Stale %', heat: 'stale_pct' },
  ];
  return (
    <div className="dashboard-section" data-testid="buildings-table-section">
      <h2>Building Performance</h2>
      <div style={{ overflowX: 'auto', marginTop: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="buildings-table">
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
              {cols.map((c) => (
                <th key={c.key} onClick={() => toggle(c.key)} style={{ padding: '8px 6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {c.label} {sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((b) => (
              <tr key={b.address} style={{ borderBottom: '1px solid #f1f5f9' }} data-testid={`building-row-${b.address}`}>
                {cols.map((c) => (
                  <td
                    key={c.key}
                    style={{ padding: '8px 6px', whiteSpace: c.key === 'address' ? 'normal' : 'nowrap', ...heat(b[c.key], c.heat) }}
                  >
                    {b[c.key] == null ? '—' : b[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Vacancy Aging ----------
function VacancyAging({ aging }) {
  const [expanded, setExpanded] = useState(null);
  if (!aging) return null;
  const buckets = ['0-7', '8-14', '15-30', '31-60', '61-90', '90+'];
  const max = Math.max(1, ...buckets.map((k) => aging.buckets[k] || 0));
  return (
    <div className="dashboard-section" data-testid="vacancy-aging-section">
      <h2>Vacancy Aging (Current Vacant-Ready Inventory)</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginTop: 8 }}>
        {buckets.map((k) => {
          const count = aging.buckets[k] || 0;
          const stale = k === '31-60' || k === '61-90' || k === '90+';
          return (
            <button
              key={k}
              onClick={() => setExpanded(expanded === k ? null : k)}
              data-testid={`aging-bucket-${k}`}
              style={{
                border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, textAlign: 'left', cursor: 'pointer',
                background: expanded === k ? '#dbeafe' : (stale && count > 0 ? '#fef2f2' : '#f8fafc'),
              }}
            >
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{k} days</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{count}</div>
              <div style={{ marginTop: 6, height: 4, background: '#e2e8f0', borderRadius: 999 }}>
                <div style={{ width: `${(count / max) * 100}%`, height: '100%', background: stale ? '#ef4444' : '#3b82f6', borderRadius: 999 }} />
              </div>
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 12, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13, color: '#475569' }}>
        <span>Median age: <strong>{aging.median_age ?? '—'} d</strong></span>
        <span>Average age: <strong>{aging.average_age ?? '—'} d</strong></span>
        {aging.oldest && (
          <span>Oldest: <strong>{aging.oldest.address} · Unit {aging.oldest.unit} ({aging.oldest.days_vacant}d)</strong></span>
        )}
      </div>
      {expanded && (
        <div style={{ marginTop: 12, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }} data-testid={`aging-details-${expanded}`}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                <th style={{ padding: '8px 6px', textAlign: 'left' }}>Building</th>
                <th style={{ padding: '8px 6px', textAlign: 'left' }}>Unit</th>
                <th style={{ padding: '8px 6px', textAlign: 'left' }}>BD/BA</th>
                <th style={{ padding: '8px 6px', textAlign: 'left' }}>Days Vacant</th>
                <th style={{ padding: '8px 6px', textAlign: 'left' }}>Vacant-Ready Date</th>
                <th style={{ padding: '8px 6px', textAlign: 'left' }}>Listing</th>
              </tr>
            </thead>
            <tbody>
              {(aging.details || []).filter((d) => d.bucket === expanded).map((d) => (
                <tr key={d.unit_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 6px' }}>{d.address}</td>
                  <td style={{ padding: '8px 6px' }}>{d.unit}</td>
                  <td style={{ padding: '8px 6px' }}>{d.bd_ba}</td>
                  <td style={{ padding: '8px 6px' }}>{d.days_vacant}</td>
                  <td style={{ padding: '8px 6px' }}>{d.vacant_ready_date?.slice(0, 10) || '—'}</td>
                  <td style={{ padding: '8px 6px' }}>{d.listing_url ? <a href={d.listing_url} target="_blank" rel="noreferrer">open</a> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Leasing Source ----------
function LeasingSource({ src }) {
  if (!src) return null;
  const total = src.total_period_leases || 0;
  const pct = (n) => total > 0 ? `${Math.round(100 * n / total)}%` : '—';
  return (
    <div className="dashboard-section" data-testid="leasing-source-section">
      <h2>Leasing Source</h2>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
        <SrcCard label="Internal" val={src.internal} pct={pct(src.internal)} color="#3b82f6" />
        <SrcCard label="External Agent" val={src.external_agent} pct={pct(src.external_agent)} color="#10b981" />
        <SrcCard label="Unknown" val={src.unknown} pct={pct(src.unknown)} color="#94a3b8" />
        <SrcCard label="Total Leases" val={total} pct="100%" color="#0f172a" />
        <SrcCard label="Attribution Coverage" val={`${src.attribution_coverage_pct}%`} color="#8b5cf6" />
      </div>
      {src.external_agents_top?.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>Top External Agents (this period)</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}>Agent</th>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}>Leases</th>
              </tr>
            </thead>
            <tbody>
              {src.external_agents_top.map((a) => (
                <tr key={a.agent} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '6px 8px' }}>{a.agent}</td>
                  <td style={{ padding: '6px 8px' }}>{a.leases}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
function SrcCard({ label, val, pct, color }) {
  return (
    <div style={{
      border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, minWidth: 140, background: '#f8fafc',
    }}>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{val}</div>
      {pct && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{pct}</div>}
    </div>
  );
}

// unused import placeholders (lucide) — keep react happy
void TrendingUp; void TrendingDown; void Minus;
